import { createMarkStore } from './mark-store'
import type { StagedAction } from './types'

/**
 * The execution fields a successful invoke establishes. Everything else about
 * the row (title, view, tags) is free to move with the server. Only the
 * "has this run?" answer is pinned.
 */
export type ExecutionSubject = Pick<StagedAction, 'id' | 'lastInvokedAt' | 'outputData' | 'resolvedAt'>
type ExecutionMark = Omit<ExecutionSubject, 'id'>

const EXECUTION_MARK_TTL_MS = 5 * 60 * 1000

/**
 * Invoking publishes the result optimistically, but the server broadcasts its
 * own `updated` for the same invoke, and any refetch already in flight when the
 * action ran answers from before it. That answer landing last is what walked the
 * row back through blank / invoking before the next fetch caught up.
 */
const store = createMarkStore<ExecutionMark, StagedAction>({
  ttlMs: EXECUTION_MARK_TTL_MS,
  // A row that has lost execution state the client already confirmed is behind, not ahead.
  isStale: (mark, row) =>
    (mark.lastInvokedAt != null && row.lastInvokedAt == null) || (mark.outputData != null && row.outputData == null),
  pin: (mark, row) => ({
    ...row,
    lastInvokedAt: mark.lastInvokedAt,
    outputData: mark.outputData,
    // The incoming value only ever adds information here: a long-running
    // action resolves after its invoke, so a server `resolvedAt` is newer
    // than the null the mark was taken with.
    resolvedAt: row.resolvedAt ?? mark.resolvedAt,
  }),
})

/**
 * Record that this action has run, with the freshest execution state the client
 * has seen for it: the invoke's own expand read, server truth rather than a guess.
 */
export function rememberExecution({ id, lastInvokedAt, outputData, resolvedAt }: ExecutionSubject): void {
  store.remember(id, { lastInvokedAt, outputData, resolvedAt })
}

/**
 * Drop the mark for an action whose execution has been deliberately taken back
 * (undo, here or in a peer window). Without this the reconciler would treat the
 * revert's `outputData: null` as a stale read and put the result straight back.
 */
export const forgetExecution = store.forget
export const clearExecutionMarks = store.clear
export const reconcileExecutionMarks = store.reconcile
